/**
 * LocationIndicator Component
 * 
 * Shows detected country and currency based on IP geolocation.
 * Useful for testing/debugging the auto-detection feature.
 */

import React from 'react';
import { Box, Tooltip, Typography, Chip, CircularProgress } from '@mui/material';
import { useUserCurrency } from '../../hooks/useUserCurrency';

// Country code to flag emoji mapping
const getCountryFlag = (countryCode: string): string => {
  if (!countryCode || countryCode.length !== 2) return '🌍';
  
  // Convert country code to regional indicator symbols
  const codePoints = countryCode
    .toUpperCase()
    .split('')
    .map(char => 127397 + char.charCodeAt(0));
  
  return String.fromCodePoint(...codePoints);
};

// Country code to name mapping (common countries)
const COUNTRY_NAMES: Record<string, string> = {
  SA: 'Saudi Arabia',
  AE: 'UAE',
  US: 'United States',
  GB: 'United Kingdom',
  CA: 'Canada',
  AU: 'Australia',
  DE: 'Germany',
  FR: 'France',
  JP: 'Japan',
  CN: 'China',
  IN: 'India',
  PK: 'Pakistan',
  EG: 'Egypt',
  KW: 'Kuwait',
  QA: 'Qatar',
  BH: 'Bahrain',
  OM: 'Oman',
  JO: 'Jordan',
  TR: 'Turkey',
  // Add more as needed
};

interface LocationIndicatorProps {
  /** Show as compact chip or detailed */
  variant?: 'chip' | 'detailed';
  /** Custom styling */
  sx?: object;
}

const LocationIndicator: React.FC<LocationIndicatorProps> = ({ 
  variant = 'chip',
  sx = {}
}) => {
  const { 
    currency, 
    detectedCountry, 
    isLoading, 
    isAutoDetected,
    currencyConfig 
  } = useUserCurrency();

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, ...sx }}>
        <CircularProgress size={14} aria-label="Detecting your location" />
        <Typography variant="caption" color="text.secondary">
          Detecting...
        </Typography>
      </Box>
    );
  }

  const flag = getCountryFlag(detectedCountry || '');
  const countryName = detectedCountry ? COUNTRY_NAMES[detectedCountry] || detectedCountry : 'Unknown';
  const currencySymbol = currencyConfig?.symbol || currency;

  const tooltipContent = (
    <Box sx={{ p: 0.5 }}>
      <Typography variant="caption" display="block">
        <strong>Country:</strong> {countryName} ({detectedCountry || '?'})
      </Typography>
      <Typography variant="caption" display="block">
        <strong>Currency:</strong> {currency} ({currencySymbol})
      </Typography>
      <Typography variant="caption" display="block" color="text.secondary">
        {isAutoDetected ? '🔄 Auto-detected from IP' : '✅ User selected'}
      </Typography>
    </Box>
  );

  if (variant === 'chip') {
    return (
      <Tooltip title={tooltipContent} arrow placement="bottom">
        <Chip
          size="small"
          label={
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <span style={{ fontSize: '1.1em' }}>{flag}</span>
              <Typography variant="caption" sx={{ fontWeight: 500 }}>
                {currency}
              </Typography>
            </Box>
          }
          sx={{
            height: 24,
            bgcolor: isAutoDetected ? 'action.hover' : 'primary.light',
            '& .MuiChip-label': { px: 1 },
            cursor: 'help',
            ...sx
          }}
        />
      </Tooltip>
    );
  }

  // Detailed variant
  return (
    <Tooltip title={tooltipContent} arrow placement="bottom">
      <Box 
        sx={{ 
          display: 'flex', 
          alignItems: 'center', 
          gap: 1,
          px: 1.5,
          py: 0.5,
          borderRadius: 1,
          bgcolor: 'action.hover',
          cursor: 'help',
          ...sx
        }}
      >
        <span style={{ fontSize: '1.3em' }}>{flag}</span>
        <Box>
          <Typography variant="caption" display="block" sx={{ fontWeight: 600, lineHeight: 1.2 }}>
            {detectedCountry || '??'}
          </Typography>
          <Typography variant="caption" display="block" color="text.secondary" sx={{ lineHeight: 1.2 }}>
            {currencySymbol}
          </Typography>
        </Box>
      </Box>
    </Tooltip>
  );
};

export default LocationIndicator;
